import type { ChangeEvent, FC, FunctionComponent, ReactElement } from 'react';
import { useEffect, useRef, useState, useCallback } from 'react';

import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';
import { useIsLargerThanWide } from '@pp/core/src/styles/hooks/media';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';
import { roundNumber } from '@pp/core/src/utils/number';

import { BetInput } from '../../components/betInput/BetInput';
import { useValidCashoutMultiplierConnected } from '../../components/gameGrid/hooks';
import { Bets } from '../../config/bets';
import { useGameDispatch } from '../../store/GameStore';
import { useAutoCashoutSlice } from '../../store/slices/autocashout/selectors';
import { autoCashoutActions } from '../../store/slices/autocashout/slice';
import { highflyerGameStateActions } from '../../store/slices/highflyerGame/slice';
import { useIsLargerThanExtraWide } from '../../styles/hooks/media';

import styles from './AutoCashoutConnected.module.css';

interface AutoCashoutInputConnectedProps {
  betCode: Bets;
  isDisabled: boolean;
}

const PostFixIcon: FunctionComponent = () => {
  const isWide = useIsLargerThanExtraWide();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={isWide ? 22 : 14}
      height={isWide ? 22 : 14}
      viewBox="0 0 14 14"
      fill="none"
    >
      <g clipPath="url(#clip0_2070_125800)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.63605 1.24927C10.234 0.651282 11.2035 0.651284 11.8015 1.24927L12.7516 2.19934C13.3496 2.79732 13.3496 3.76686 12.7516 4.36485L5.21813 11.8983C5.03412 12.0823 4.80625 12.2164 4.55604 12.2879L1.71155 13.1006C1.48239 13.1661 1.23576 13.1021 1.06723 12.9336C0.898706 12.7651 0.834793 12.5185 0.900263 12.2893L1.71298 9.44481C1.78447 9.19464 1.91854 8.9667 2.10256 8.78269L9.63605 1.24927ZM10.8735 2.17735C10.788 2.09193 10.6495 2.09193 10.5641 2.17735L9.4593 3.28209L10.7188 4.54152L11.8236 3.43677C11.909 3.35135 11.909 3.21284 11.8236 3.12742L10.8735 2.17735ZM9.79066 5.46959L8.53127 4.21017L3.03063 9.7108C3.00435 9.73705 2.9852 9.7696 2.97498 9.80539L2.48678 11.5141L4.19547 11.0258C4.23121 11.0157 4.26376 10.9965 4.29006 10.9702L9.79066 5.46959Z"
          fill="white"
        />
      </g>
    </svg>
  );
};

export const AutoCashoutInputConnected: FC<AutoCashoutInputConnectedProps> = ({
  betCode,
  isDisabled,
}): ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useGameDispatch();
  const [tooltipText, setTooltipText] = useState('EDIT');
  const { inputFieldValue, actualMultplier } = useValidCashoutMultiplierConnected(betCode);
  const [isEditing, setIsEditing] = useState(false);
  const lastValue = useAutoCashoutSlice.getLastValue(betCode);
  const onFocusTimerId = useRef<number>();
  const isDesktop = useIsLargerThanWide();
  const minLimits = 1.01;
  const customMultiplierCap = useConfigSlice.tableConfig();
  const maxLimits = customMultiplierCap?.customMultiplierCap ? customMultiplierCap.customMultiplierCap : 100;
  const onFocus = useCallback((): void => {
    setTooltipText('HIGHFLYER_BET_LIMIT_TOOLTIP');
    setIsEditing(true);
    if (!isDesktop) {
      dispatch(highflyerGameStateActions.setToggleKeypad({ autoCashOut: true, betCode: betCode }));
    } else {
      window.clearTimeout(onFocusTimerId.current);
      onFocusTimerId.current = window.setTimeout(() => {
        inputRef?.current?.select();
      }, 0);
    }
  }, [isDesktop, dispatch, betCode]);

  const onBlur = useCallback((): void => {
    setTooltipText('EDIT');
    setIsEditing(false);
    inputRef?.current?.blur();
    dispatch(
      autoCashoutActions.setCashoutMultiplier({
        betCode: betCode,
        cashoutMultiplier: roundNumber(actualMultplier, 2).toFixed(2),
        isAutoCashoutFinalized: true,
      })
    );
  }, [dispatch, betCode, actualMultplier]);

  const onEscape = useCallback(() => {
    onBlur();
    dispatch(
      autoCashoutActions.setCashoutMultiplier({
        betCode: betCode,
        cashoutMultiplier: lastValue,
        isAutoCashoutFinalized: true,
      })
    );
  }, [onBlur, betCode, dispatch, lastValue]);

  const onKeyPressHandler = useCallback(
    (event: any) => {
      if (!inputRef.current) return;
      if (event.key === 'Enter') {
        inputRef.current?.blur();
      }
      if (event.key === 'Escape') {
        onEscape();
      }
    },
    [onEscape]
  );

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      dispatch(
        autoCashoutActions.setCashoutMultiplier({
          betCode: betCode,
          cashoutMultiplier: event.target.value,
        })
      );
    },
    [dispatch, betCode]
  );

  useEffect(() => {
    if (!inputRef.current) return;

    const childEl = inputRef.current;
    childEl.addEventListener('focus', onFocus);
    childEl.addEventListener('blur', onBlur);
    childEl.addEventListener('keyup', onKeyPressHandler);

    return () => {
      childEl.removeEventListener('keyup', onKeyPressHandler);
      childEl.removeEventListener('blur', onBlur);
      childEl.removeEventListener('focus', onFocus);
    };
  }, [onFocus, onBlur, onKeyPressHandler]);

  const mods_inputArea = useModifiers(styles, 'input_area', {
    editing: isEditing,
  });

  return (
    <BetInput
      inputRef={inputRef}
      value={`${inputFieldValue}${!isEditing ? 'x' : ''}`}
      onChange={handleOnChange}
      containerClass={`${styles.auto_cashout_input} ${mods_inputArea}`}
      postfix={<PostFixIcon />}
      tooltipMsg={tooltipText}
      isDisabled={isDisabled}
      attrs={[`${minLimits}x`, `${maxLimits}x`]}
    />
  );
};
